import React, { useEffect, useState } from "react";
import { Plus, Trash2, FileText, Download, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Section, Btn, Input, Select, Field, cardCls, EmptyState } from "@/components/admin/ui";
import Modal from "@/components/admin/Modal";
import FileUpload from "@/components/admin/FileUpload";

const CATEGORIES = ["Company", "Insurance", "Licences", "Policies", "Contracts", "Supplier", "Staff", "Marketing", "Financial", "Legal"];
const EMPTY = { title: "", category: "Company", venture_id: "", file_id: "", notes: "" };

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploaded, setUploaded] = useState(null);

  const load = () => api.get("/admin/documents", { params: catFilter ? { category: catFilter } : {} }).then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, [catFilter]);
  useEffect(() => { api.get("/admin/ventures").then(({ data }) => setVentures(data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm(EMPTY); setUploaded(null); setEditing(true); };

  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    if (!form.file_id) { toast.error("Please upload a file"); return; }
    await api.post("/admin/documents", form);
    toast.success("Document saved (stored securely & privately)");
    setEditing(false); load();
  };
  const remove = async (d) => { if (!window.confirm("Delete document record?")) return; await api.delete(`/admin/documents/${d.id}`); load(); };
  const vName = (id) => ventures.find((v) => v.id === id)?.name || "Group";

  const download = async (d) => {
    try {
      const res = await api.get(`/files/${d.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.title; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="admin-documents">
      <Section title="Document Centre" subtitle="Company, insurance, licences, policies, contracts and more — stored securely and never publicly accessible." action={<Btn data-testid="doc-add" onClick={openNew}><Plus size={15} /> Upload Document</Btn>}>
        <div className={`${cardCls} p-4 mb-6 flex items-center gap-2 text-xs text-bd-ivory/50`}><Lock size={14} className="text-bd-gold" /> All documents are private. Access requires an authenticated group login.</div>
        <div className="mb-6 max-w-xs">
          <Select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}><option value="">All categories</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</Select>
        </div>
        {docs.length === 0 ? <EmptyState>No documents yet.</EmptyState> : (
          <div className={`${cardCls} divide-y divide-white/5`}>
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-4 p-4 hover:bg-white/[0.02]">
                <FileText size={20} className="text-bd-gold flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-bd-ivory truncate">{d.title}</p>
                  <p className="text-xs text-bd-ivory/40">{d.category} · {vName(d.venture_id)}</p>
                </div>
                {d.file_id && <button onClick={() => download(d)} className="p-2 text-bd-ivory/40 hover:text-bd-gold"><Download size={16} /></button>}
                <button onClick={() => remove(d)} className="p-2 text-bd-ivory/40 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Modal open={editing} onClose={() => setEditing(false)} title="Upload Document" testId="doc-modal">
        <div className="space-y-5">
          <Field label="Title"><Input data-testid="doc-title" value={form.title} onChange={set("title")} /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Category"><Select value={form.category} onChange={set("category")}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</Select></Field>
            <Field label="Linked venture"><Select value={form.venture_id || ""} onChange={set("venture_id")}><option value="">Group (none)</option>{ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</Select></Field>
          </div>
          <Field label="File (stored privately)">
            <FileUpload
              value={uploaded}
              visibility="private"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
              preview={false}
              label="Upload file"
              onChange={(url, data) => { setForm((f) => ({ ...f, file_id: data.id })); setUploaded(data.original_filename); }}
            />
            {uploaded && <p className="text-xs text-bd-gold mt-2">Attached: {uploaded}</p>}
          </Field>
          <div className="flex justify-end gap-3">
            <Btn variant="outline" onClick={() => setEditing(false)}>Cancel</Btn>
            <Btn data-testid="doc-save" onClick={save}>Save Document</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
